import { Link, Navigate, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth-context";

/**
 * Páginas de estado — os becos sem saída do ProtectedRoute.
 * Cada uma explica POR QUE motivo o utilizador parou aqui e o que fazer a seguir.
 */

function Estado({
  titulo,
  descricao,
  children,
}: {
  titulo: string;
  descricao: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex min-h-svh items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{titulo}</CardTitle>
          <CardDescription>{descricao}</CardDescription>
        </CardHeader>
        {children ? <CardContent className="flex gap-2">{children}</CardContent> : null}
      </Card>
    </div>
  );
}

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  Terminar sessão a partir de uma página de estado
 *
 *  Estas páginas vivem FORA do `ProtectedRoute` — têm de viver, senão a guarda
 *  que redirige para cá redirigia para si própria em ciclo. O efeito colateral
 *  é que nada as tira do ecrã quando a sessão desaparece: `signOut` limpava o
 *  estado e a página ficava exatamente onde estava, com o mesmo texto e o mesmo
 *  botão, dando a impressão de que o clique não tinha feito nada.
 *
 *  Daí a navegação explícita. `replace` para que o botão de recuar do browser
 *  não traga a pessoa de volta a um beco onde já não tem sessão.
 * ─────────────────────────────────────────────────────────────────────────────
 */
function BotaoSair() {
  const { signOut } = useAuth();
  const navegar = useNavigate();

  return (
    <Button
      variant="outline"
      onClick={async () => {
        await signOut();
        void navegar("/login", { replace: true });
      }}
    >
      Terminar sessão
    </Button>
  );
}

/**
 * Pela mesma razão: quem chegue a estes endereços por URL, sem sessão nenhuma,
 * veria uma explicação sobre uma conta que não existe. Vai para o login.
 */
function ExigirSessao({ children }: { children: React.ReactNode }) {
  const { session, loading, hydrating } = useAuth();
  if (loading || hydrating) {
    return (
      <div className="flex min-h-svh items-center justify-center p-6">
        <Skeleton className="h-40 w-full max-w-md" />
      </div>
    );
  }
  if (!session) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function AguardandoAprovacao() {
  const { profile } = useAuth();
  return (
    <ExigirSessao>
      <Estado
        titulo="Conta pendente de aprovação"
        descricao={`A conta ${profile?.email ?? ""} foi criada mas ainda não foi aprovada. Um administrador do programa tem de a aprovar e atribuir-lhe um projeto.`}
      >
        <BotaoSair />
      </Estado>
    </ExigirSessao>
  );
}

export function SemProjeto() {
  return (
    <ExigirSessao>
      <Estado
        titulo="Sem projeto atribuído"
        descricao="A conta está aprovada mas não é membro de nenhum projeto. Peça a um administrador que o adicione."
      >
        <BotaoSair />
      </Estado>
    </ExigirSessao>
  );
}

export function SemPermissao() {
  return (
    <Estado
      titulo="Sem permissão"
      descricao="A conta não tem permissão de leitura neste módulo. Se acha que devia ter, fale com um administrador do programa."
    >
      <Button asChild variant="outline">
        <Link to="/">Voltar ao início</Link>
      </Button>
    </Estado>
  );
}

export function NaoEncontrado() {
  return (
    <Estado titulo="Página não encontrada" descricao="O endereço pedido não existe.">
      <Button asChild variant="outline">
        <Link to="/">Voltar ao início</Link>
      </Button>
    </Estado>
  );
}
